import { useEffect, useRef, useState } from "react";
import { motion, useMotionTemplate, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Flag, Flashlight, MousePointerClick } from "lucide-react";
import { ABOUT, IMAGES } from "../data/content";
// IMAGES.dawnCamp is served locally: /images/dawn-camp.jpg
IMAGES.dawnCamp = IMAGES.dawnCamp || "/images/dawn-camp.jpg";
import { Reveal, TopoLines } from "./ui-bits";

/* Climb through the night into the dawn:
   sliding the lantern slider advances the clock (02:00 -> 06:30) and warms the air (-12 -> -2 C),
   wiping the scene from the night camp to sunrise. The wipe moves only when the slider is dragged. */
function NightAscent() {
  const bx = useMotionValue(0.35);
  const pos = useSpring(bx, { stiffness: 120, damping: 23 });
  const posPct = useTransform(pos, (v) => `${v * 100}%`);
  const rightPct = useTransform(pos, (v) => `${(1 - v) * 100}%`);
  const clip = useMotionTemplate`inset(0 ${rightPct} 0 0)`;
  const clock = useTransform(pos, (v) => {
    const m = 120 + v * 270;
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(Math.round(m % 60)).padStart(2, "0")}`;
  });
  const temp = useTransform(pos, (v) => `${Math.round(-12 + v * 10)}°C`);

  const boxRef = useRef(null);
  const sliderRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const setSlide = (v) => {
    const c = Math.min(1, Math.max(0, v));
    bx.set(c);
    sliderRef.current?.setAttribute("aria-valuenow", String(Math.round(c * 100)));
  };

  /* the wipe only moves while the lantern slider is dragged — hovering the scene does nothing */
  useEffect(() => {
    if (!dragging) return;
    const slide = (e) => {
      const r = boxRef.current?.getBoundingClientRect();
      if (r) setSlide((e.clientX - r.left) / r.width);
    };
    const stop = () => setDragging(false);
    window.addEventListener("pointermove", slide);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    return () => {
      window.removeEventListener("pointermove", slide);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
    };
  }, [dragging]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") { e.preventDefault(); setSlide(bx.get() - 0.05); }
    if (e.key === "ArrowRight" || e.key === "ArrowUp") { e.preventDefault(); setSlide(bx.get() + 0.05); }
  };

  return (
    <div
      ref={boxRef}
      data-testid="about-night-ascent"
      className="relative mt-14 h-52 select-none overflow-hidden border border-cream/12 md:h-64"
    >
      {/* night base photograph */}
      <img src={IMAGES.aboutCamp} alt="Night camp under the stars" className="absolute inset-0 h-full w-full scale-[1.04] object-cover" />

      {/* dawn reveal — right of the light */}
      <motion.div style={{ clipPath: clip }} className="absolute inset-0" data-testid="night-dawn-reveal">
        <img src={IMAGES.dawnCamp} alt="The same camp at sunrise" className="absolute inset-0 h-full w-full scale-[1.04] object-cover" />
      </motion.div>

      {/* the light — draggable lantern slider (the only control that moves the wipe) */}
      <motion.div style={{ left: posPct }} className="absolute inset-y-0 z-10 w-px bg-cream/90">
        <span className="absolute inset-y-0 -left-3 w-3 bg-gradient-to-r from-transparent to-ember/20" />
        <div
          ref={sliderRef}
          role="slider"
          tabIndex={0}
          aria-label="Slide the lantern — reveal dawn over the night camp"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={35}
          onPointerDown={() => setDragging(true)}
          onKeyDown={onKeyDown}
          data-testid="night-dawn-handle"
          className="absolute inset-y-0 -left-5 flex w-10 cursor-ew-resize touch-none items-center justify-center focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-[-1px] focus-visible:outline-ember/60"
        >
          <span
            className={`flex h-10 w-10 items-center justify-center rounded-full border bg-ink/90 backdrop-blur-sm transition-[transform,box-shadow,border-color] duration-200 ${
              dragging
                ? "scale-110 border-ember shadow-[0_0_30px_rgba(255,107,55,0.7)]"
                : "border-ember/70 shadow-[0_0_24px_rgba(255,107,55,0.45)]"
            }`}
          >
            <Flashlight className="h-4 w-4 text-ember" />
          </span>
        </div>
      </motion.div>

      {/* legibility scrim + vignettes */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-night/60 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-night/60 to-transparent" />

      {/* overlays */}
      <p className="pointer-events-none absolute left-4 top-4 z-20 font-mono text-[9px] tracking-[0.3em] text-cream/80 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
        FIELD NOTE 02-A — THE NIGHT ASCENT
      </p>
      <div className="pointer-events-none absolute right-4 top-4 z-20 flex gap-2 font-mono text-[9px] tracking-[0.15em]" data-testid="night-clock">
        <span className="border border-white/15 bg-night/70 px-2 py-1 text-cream/85 backdrop-blur-sm">TIME <motion.span className="font-bold text-ember">{clock}</motion.span></span>
        <span className="border border-white/15 bg-night/70 px-2 py-1 text-cream/85 backdrop-blur-sm">TEMP <motion.span className="font-bold text-ember">{temp}</motion.span></span>
      </div>
      <p className="pointer-events-none absolute bottom-4 left-4 z-20 font-mono text-[9px] tracking-[0.25em] text-cream/80 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
        SLIDE THE LANTERN — CLIMB THROUGH THE NIGHT INTO DAWN
      </p>
      <p className="pointer-events-none absolute bottom-4 right-4 z-20 hidden font-mono text-[9px] tracking-[0.25em] text-cream/80 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)] sm:block">
        30.3165° N / 78.0322° E
      </p>
    </div>
  );
}

/* ── the expedition permit: a paper ID card you tilt, inspect and flip ── */

const PERMIT_ROWS = [
  ["CURRENT", "BRANDING — APL APOLLO"],
  ["CRAFT", "DESIGN · WEB · CONTENT · MARKETING"],
  ["GROWTH", "SEO · ECOMMERCE LISTINGS · CATALOGUES"],
  ["PROJECTS", "4 CLIENT EXPEDITIONS — LIVE"],
];

const PERMIT_TREK = [
  ["TREKKING SINCE", "2017 — ON THE TRAIL"],
  ["EXPEDITIONS", "50+ TREKS · 2-3 PEAK EXPEDITIONS"],
  ["FIELD ROLE", "OUTDOOR LEADER — INDIAHIKES"],
  ["RANGE", "HIMALAYA · 30.3165° N"],
];

function PermitFront() {
  return (
    <div className="group absolute inset-0 overflow-hidden border-2 border-[#182018]/60 bg-[#F2ECDD] p-5 text-[#182018] shadow-[0_34px_70px_-32px_rgba(0,0,0,0.95)] [backface-visibility:hidden]">
      {/* hidden security layer, revealed on hover */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-500 group-hover:opacity-100" data-testid="about-permit-uv">
        <TopoLines className="absolute inset-0 h-full w-full opacity-30" />
        <span className="relative -rotate-[8deg] border-2 border-[#E85A24]/70 bg-[#F2ECDD]/60 px-4 py-1.5 font-mono text-[10px] font-bold tracking-[0.35em] text-[#E85A24]">
          GENUINE — FIELD TESTED
        </span>
      </div>

      <div className="flex items-center justify-between border-b-2 border-[#182018]/70 pb-2.5">
        <span className="font-mono text-[9px] font-bold tracking-[0.3em]">EXPEDITION PERMIT</span>
        <span className="font-mono text-[9px] font-bold tracking-[0.2em] text-[#E85A24]">N° 002 / HMK</span>
      </div>

      <div className="mt-4 flex gap-4">
        <div className="relative h-28 w-24 shrink-0 overflow-hidden border-2 border-[#182018]/80">
          <img src={IMAGES.holderPhoto} alt="Permit holder — Amit Rawat" className="h-full w-full object-cover object-top" />
          <span className="absolute inset-x-0 bottom-0 bg-[#182018]/85 py-0.5 text-center font-mono text-[6px] tracking-[0.3em] text-[#F2ECDD]">HOLDER</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-[26px] font-black uppercase leading-[0.95]">Amit<br />Rawat</p>
          <p className="mt-1.5 font-mono text-[8px] font-bold leading-relaxed tracking-[0.18em] text-[#182018]/70">GRAPHIC DESIGNER<br />& WEB DESIGNER</p>
          <p className="mt-2 inline-block bg-[#E85A24] px-1.5 py-0.5 font-mono text-[7px] font-bold tracking-[0.2em] text-[#F2ECDD]">CREATIVE DIVISION</p>
        </div>
      </div>

      <div className="mt-4">
        {PERMIT_ROWS.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-4 border-b border-dashed border-[#182018]/25 py-[7px] font-mono text-[8px] tracking-[0.15em] last:border-0">
            <span className="text-[#182018]/60">{k}</span>
            <span className="text-right font-bold">{v}</span>
          </div>
        ))}
      </div>

      <div className="mt-auto pt-3">
        <div className="flex items-end gap-3">
          <div className="h-8 flex-1 opacity-90" style={{ backgroundImage: "repeating-linear-gradient(90deg, #182018 0 2px, transparent 2px 5px, #182018 5px 6px, transparent 6px 9px, #182018 9px 12px, transparent 12px 14px)" }} />
          <span className="font-mono text-[8px] font-bold tracking-[0.2em]">002-2017-HMK</span>
        </div>
        <div className="mt-2.5 flex items-center justify-between">
          <span className="flex items-center gap-1.5 font-mono text-[8px] font-bold tracking-[0.2em] text-[#182018]/70">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#E85A24]" /> OPEN TO NEW EXPEDITIONS
          </span>
          <span className="-rotate-6 border-2 border-[#E85A24] px-2 py-0.5 font-mono text-[8px] font-bold tracking-[0.25em] text-[#E85A24]">VALID</span>
        </div>
      </div>
    </div>
  );
}

function PermitBack() {
  return (
    <div className="absolute inset-0 overflow-hidden border-2 border-[#182018]/60 bg-[#F2ECDD] p-5 text-[#182018] shadow-[0_34px_70px_-32px_rgba(0,0,0,0.95)] [backface-visibility:hidden] [transform:rotateY(180deg)]">
      <TopoLines className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.12]" />
      <div className="flex items-center justify-between border-b-2 border-[#182018]/70 pb-2.5">
        <span className="font-mono text-[9px] font-bold tracking-[0.3em]">MOUNTAIN LEDGER</span>
        <span className="font-mono text-[9px] font-bold tracking-[0.2em] text-[#E85A24]">TREK RECORD — PAGE 2 / 2</span>
      </div>

      <div className="mt-4">
        {PERMIT_TREK.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-4 border-b border-dashed border-[#182018]/25 py-[9px] font-mono text-[8px] tracking-[0.15em] last:border-0">
            <span className="text-[#182018]/60">{k}</span>
            <span className="text-right font-bold">{v}</span>
          </div>
        ))}
      </div>

      {/* little route doodle */}
      <svg viewBox="0 0 300 40" className="mx-auto mt-3 h-9 w-3/4 text-[#E85A24]">
        <path d="M6 34 C 60 30, 90 20, 130 18 S 220 10, 294 6" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="5 6" strokeLinecap="round" />
        {[[6, 34], [130, 18], [294, 6]].map(([cx, cy]) => (
          <circle key={cx} cx={cx} cy={cy} r="3.5" fill="currentColor" />
        ))}
      </svg>

      <div className="mt-auto flex items-end justify-between pt-3">
        <div>
          {/* mountain line art above the signature */}
          <svg viewBox="0 0 120 26" className="mb-1.5 h-7 w-28 text-[#E85A24]">
            <motion.path
              d="M4 22 L20 8 L29 16 L44 4 L60 19 L74 10 L88 21 L100 13 L116 21"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2, delay: 0.4 }}
            />
            <circle cx="104" cy="5" r="2.5" fill="currentColor" opacity="0.85" />
          </svg>
          <p className="-rotate-2 text-3xl font-semibold leading-none text-[#182018]" style={{ fontFamily: "'Caveat', cursive" }} data-testid="about-permit-signature">
            Amit Rawat
          </p>
          <p className="mt-1 font-mono text-[7px] tracking-[0.25em] text-[#182018]/60">SIGNED ON THE TRAIL</p>
        </div>
        <span className="flex h-20 w-20 rotate-12 flex-col items-center justify-center rounded-full border-[3px] border-[#E85A24] p-2 text-center font-mono text-[8px] font-bold leading-tight tracking-[0.15em] text-[#E85A24]">
          50+ TREKS<br />FIELD<br />TESTED
        </span>
      </div>
    </div>
  );
}

function PermitCard() {
  const [flipped, setFlipped] = useState(false);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 160, damping: 18 });
  const sry = useSpring(ry, { stiffness: 160, damping: 18 });
  const gx = useMotionValue(50);
  const gy = useMotionValue(50);
  const glare = useMotionTemplate`radial-gradient(circle at ${gx}% ${gy}%, rgba(245,240,225,0.30), rgba(245,240,225,0) 55%)`;

  const onMove = (e) => {
    if (e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    ry.set((px - 0.5) * 14);
    rx.set((0.5 - py) * 10);
    gx.set(px * 100);
    gy.set(py * 100);
  };
  const onLeave = () => { rx.set(0); ry.set(0); gx.set(50); gy.set(50); };

  return (
    <div className="[perspective:1200px]" data-testid="about-permit">
      <motion.div
        onPointerMove={onMove}
        onPointerLeave={onLeave}
        onClick={() => setFlipped((f) => !f)}
        style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
        className="relative mx-auto h-[460px] w-full max-w-[360px] cursor-pointer select-none"
        data-testid="about-permit-card"
        data-flipped={flipped}
      >
        <motion.div
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative h-full w-full [transform-style:preserve-3d]"
        >
          <PermitFront />
          <PermitBack />
        </motion.div>
        <motion.div
          style={{ background: glare }}
          className="pointer-events-none absolute inset-0 z-10 [transform:translateZ(1px)]"
          data-testid="about-permit-glare"
        />
      </motion.div>
    </div>
  );
}

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative scroll-mt-24 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {/* the single header */}
        <Reveal>
          <p className="flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-ember md:text-[11px]">
            <span className="h-px w-10 bg-ember" />02 / ABOUT — BASE CAMP BRIEFING
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-7 font-display text-5xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl" data-testid="about-heading">
            I climb <span className="text-ember">briefs</span><br />the way I climb<br />
            <span className="text-outline whitespace-nowrap">
              mountains
              <Flag className="ml-2 inline h-[0.6em] w-[0.6em] -translate-y-[0.05em] fill-ember text-ember sm:ml-3 sm:h-[0.68em] sm:w-[0.68em]" data-testid="about-summit-flag" />
            </span>
          </h2>
        </Reveal>

        {/* story + the interactive permit */}
        <div className="mt-10 grid items-stretch gap-10 lg:grid-cols-[1fr_360px] lg:gap-20">
          <Reveal delay={0.14} className="h-full">
            <div className="flex h-full flex-col justify-between gap-6">
              <div>
                <p className="text-lg leading-relaxed text-cream/85">{ABOUT.p1}</p>
                <p className="mt-4 leading-relaxed text-cream/60">{ABOUT.p2}</p>
              </div>
              <div className="max-w-xl border-l-2 border-ember bg-ink/50 p-5" data-testid="about-positioning">
                <p className="font-display text-lg font-bold leading-snug text-white md:text-xl">{ABOUT.positioning}</p>
              </div>
              <div className="flex items-center gap-3 border border-cream/15 bg-ink/50 px-4 py-3" data-testid="about-permit-hint">
                <MousePointerClick className="h-4 w-4 shrink-0 text-ember" />
                <span className="font-mono text-[9px] tracking-[0.22em] text-cream/60">
                  TILT THE PERMIT TO INSPECT IT — CLICK TO TURN IT OVER
                </span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2} className="h-full">
            <PermitCard />
          </Reveal>
        </div>

        {/* interactive night -> dawn strip */}
        <Reveal delay={0.1}>
          <NightAscent />
        </Reveal>
      </div>
    </section>
  );
}
